import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Outlet, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../state/store";
import { setFilters as setCampaignFilters } from "../../state/campaign/campaign.slice";
import { getAllCampaigns } from "../../services/CampaignService";
import { Campaign } from "../../types/campaign.types";
import { PageLayout } from "../../components/layout";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import SearchInput from "../../components/Search/SearchInput";
import ListTypes from "../../components/ListTypes";
import { Table, TableColumn } from "../../components/Table";
import ProfileComponent from "../../components/Campaign/ProfileComponent";
import NewCampainCreateModel from "../../components/Campaign/NewCampainCreateModel";
import CardTable from "../../components/DataCardTable/CardTable";
import CardImage from "../../components/DataCardTable/CardImage";
import CardTitle from "../../components/DataCardTable/CardTitle";
import CardSubTitle from "../../components/DataCardTable/CardSubTitle";
import CardLocation from "../../components/DataCardTable/CardLocation";
import CardDetails from "../../components/DataCardTable/CardDetails";
import CampainCardDetails from "../../components/Campaign/CampainCardDetails";
import {FaPlus } from 'react-icons/fa';
import Pagination from '../../components/Pagination';

import { formatDate } from '../../utils/dateFormatter';
import { debounce } from '../../utils/debounce';

const PAGE_SIZE = 20;


const CampaignPageStandardized: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch<AppDispatch>();

    // Redux state
    const campaigns = useSelector((state: RootState) => state.campaign.campaignList);
    const loading = useSelector((state: RootState) => state.campaign.loading);
    const showDeactivated = useSelector((state: RootState) => state.campaign.filters.showDeactivated ?? false);

    // Local UI state
    const [layout, setLayout] = useState("LISTING");
    const [inputValue, setInputValue] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [openCampaignCreateModal, setOpenCampaignCreateModal] = useState(false);
    const [sortBy, setSortBy] = useState<string | undefined>();
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [resetPagination, setResetPagination] = useState(false);

    const debouncedSearch = useMemo(
        () => debounce((value: string) => {
            setSearchTerm(value);
        }, 400),
        []
    );

    const handleInputChange = (value: string) => {
        setInputValue(value);
        debouncedSearch(value);
    };

    // Fetch campaigns via Redux
    useEffect(() => {
        dispatch(getAllCampaigns({ showDeactivated }));
    }, [dispatch, showDeactivated]);

    const handleCampaignClick = (campaign: Campaign) => {
        if (campaign && campaign.id) {
            navigate(`${campaign.id}`);
        }
    };

    const filteredCampaigns = useMemo(() => {
        let result = [...campaigns];

        // Apply text filtering
        if (searchTerm && searchTerm !== "") {
            const searchTermLower = searchTerm.toLowerCase().trim();
            
            result = result.filter((campaign) => {
                const nameMatch = campaign.name?.toLowerCase().includes(searchTermLower);
                return nameMatch;
            });
        }
        
        // Apply sorting
        if (sortBy) {
            result.sort((a, b) => {
                const direction = sortDirection === 'asc' ? 1 : -1;
                
                switch (sortBy) {
                    case 'name':
                        return (a.name?.localeCompare(b.name || '') || 0) * direction;
                    case 'partner':
                        return (a.partner?.localeCompare(b.partner || '') || 0) * direction;
                    case 'date':
                        return (new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime()) * direction;
                    case 'barsdonated':
                        return ((a.amountCollected || 0) - (b.amountCollected || 0)) * direction;
                    case 'goal':
                        return ((a.targetAmount || 0) - (b.targetAmount || 0)) * direction;
                    default:
                        return 0;
                }
            });
        }
        
        return result;
    }, [campaigns, searchTerm, sortBy, sortDirection]);

    const paginatedCampaigns = useMemo(
        () => filteredCampaigns.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [filteredCampaigns, currentPage]
    );

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
        setResetPagination((v) => !v);
    }, [filteredCampaigns]);

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortBy(column);
        setSortDirection(direction);
    };

    const handleCampaignCreateModalClose = () => {
        setOpenCampaignCreateModal(false);
        dispatch(getAllCampaigns({ showDeactivated }));
    };

    // Define columns for our Table component
    const campaignColumns: TableColumn<Campaign>[] = [
        {
            id: 'name',
            label: 'Name',
            field: 'name',
            sortable: true,
            format: (_, campaign) => (
                <ProfileComponent
                    name={campaign.name || ''}
                    imageSrc={campaign.banner || ''}
                    location={campaign.locationText || ''}
                    completed={campaign.completed || false}
                />
            ),
            priority: 'high',
            width: '25%'
        },
        {
            id: 'date',
            label: 'Date Created',
            field: 'startDate',
            sortable: true,
            format: (value) => formatDate(value),
            priority: 'medium',
            width: '15%'
        },
        {
            id: 'partner',
            label: 'Local Partner',
            field: 'partner',
            sortable: true,
            priority: 'medium',
            width: '15%'
        },
        {
            id: 'shipment',
            label: 'Shipment',
            field: 'shipments',
            format: (value) => value || 0,
            priority: 'medium'
        },
        {
            id: 'donation',
            label: 'Donation',
            field: 'donationAidUnits',
            priority: 'medium'
        },
        {
            id: 'barsdonated',
            label: 'Bars Donated',
            field: 'amountCollected',
            sortable: true,
            priority: 'high'
        },
        {
            id: 'goal',
            label: 'Goal',
            field: 'targetAmount',
            sortable: true,
            priority: 'medium'
        },
        {
            id: 'status',
            label: 'Active',
            field: 'isActive',
            format: (value) => (
                <Badge 
                    label={value ? "Active" : "Inactive"} 
                    variant={value ? "success" : "default"} 
                    size="md" 
                />
            ),
            type: 'badge',
            priority: 'high'
        }
    ];

    // Render the navigation breadcrumb if a campaign is selected
    const renderCampaignDetail = () => {
        if (!id) return null;
        
        return (
            <>
                <nav className="sticky top-16 z-20 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-gray-200 dark:border-white/10 flex items-center space-x-2 py-4 px-3">
                    <span
                        className="text-gray-600 dark:text-gray-300 hover:underline cursor-pointer"
                        onClick={() => navigate("/campaigns")}
                    >
                        Campaigns
                    </span>
                    <span className="text-gray-400 dark:text-gray-500">{'›'}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {campaigns.find(c => String(c.id) === id)?.name || id}
                    </span>
                </nav>
                <Outlet />
            </>
        );
    };

    // Render actions for PageLayout
    const renderActions = () => (
        <Button
            variant="primary"
            onClick={() => setOpenCampaignCreateModal(true)}
            fullWidth={false}
            size="sm"
            startIcon={<FaPlus />}
        >
            New campaign
        </Button>
    );

    // Render search box and controls for PageLayout
    const renderSearchBox = () => (
        <div className="w-full space-y-4">
            <SearchInput
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Search for campaigns by name"
                onClear={() => {
                    setInputValue("");
                    setSearchTerm("");
                }}
                debounce={0} // Disable component's internal debouncing
            />
            
            {/* Toggle and layout switcher */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <label className="inline-flex relative items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={showDeactivated}
                            onChange={() => dispatch(setCampaignFilters({ showDeactivated: !showDeactivated }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-primary-100 transition-all"></div>
                        <div className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${showDeactivated ? "translate-x-5" : ""}`}></div>
                    </label>
                    <span className="text-gray-700 dark:text-gray-300 select-none">Show deactivated</span>
                </div>
                <div>
                    <ListTypes setLayout={setLayout} />
                </div>
            </div>
        </div>
    );

    // Render pagination controls for PageLayout
    const renderPagination = () =>
        filteredCampaigns.length > PAGE_SIZE ? (
            <Pagination
                totalRecords={filteredCampaigns.length}
                perPageCount={PAGE_SIZE}
                reset={resetPagination}
                setPerPageDataSet={(start, _end) => {
                    const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE));
                    setCurrentPage(nextPage);
                }}
            />
        ) : null;

    // Render the main campaign listing content
    const renderCampaignListing = () => {
        if (id) return null;
        
        return (
            <PageLayout
                title="Campaigns"
                actions={renderActions()}
                filters={renderSearchBox()}
                footer={renderPagination()}
            >
                {layout === "LISTING" ? (
                    <div className="overflow-x-auto">
                        <Table
                            data={paginatedCampaigns}
                            columns={campaignColumns}
                            loading={loading}
                            hoverable={true}
                            striped={false}
                            emptyMessage="No campaigns found"
                            fixedHeader={true}
                            onRowClick={handleCampaignClick}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                            className="w-full"
                        />
                    </div>
                ) : (
                    <CardTable
                        dataSet={paginatedCampaigns.map(d => ({
                            data: [
                                {
                                    name: "name",
                                    type: "component",
                                    value: <CardImage imageSrc={d.banner || ''} completed={d.completed}/>,
                                    searchTerm: `${d.name} ${d.locationText}`,
                                },
                                {
                                    name: "title",
                                    type: "component",
                                    value: <CardTitle title={d.name} />,
                                },
                                {
                                    name: "subtitle",
                                    type: "component",
                                    value: (
                                        <CardSubTitle
                                            subtitle={<CardLocation location={d.locationText || "Not found"} />}
                                        />
                                    ),
                                },
                                {
                                    name: "details",
                                    type: "text",
                                    value: (
                                        <CardDetails
                                            details={
                                                <CampainCardDetails
                                                    description={d.description}
                                                    amountCollected={d.amountCollected}
                                                    targetAmount={d.targetAmount}
                                                />
                                            }
                                        />
                                    ),
                                },
                            ],
                            style: "hover:cursor-pointer hover:bg-primary-10",
                            onClick: () => handleCampaignClick(d),
                            id: d.id?.toString()
                        }))}
                        reset={false}
                    />
                )}
            </PageLayout>
        );
    };

    return (
        <>
            {renderCampaignDetail()}
            {renderCampaignListing()}
            <NewCampainCreateModel
                open={openCampaignCreateModal}
                onClose={handleCampaignCreateModalClose}
            />
        </>
    );
};

export default CampaignPageStandardized;